import React, { useContext, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import Container from 'components/Container';
import IndustrySelector from 'components/IndustrySelector';
import { useTranslation } from 'services/localization';
import { CustomerPersona, IndustryCategory } from 'common/services/analytics/tractionAnalytics';
import RentleLogo from 'common/components/RentleLogo';
import gradientPattern from 'common/assets/signup-wizard-gradient.png';
import { RegisterContext } from './RegisterContext';
import { logEvent, newAdditionalSignUpInfoEvent } from 'common/services/analytics';
import { api } from 'common/frontend/api';
import { push, Routes } from 'routing';
import { Redirect } from 'react-router-dom';
import { motion } from 'framer-motion';
import CustomerPersonaPicker from './CustomerPersonaPicker';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { useSelector } from 'react-redux';
import errorHandler from 'common/services/errorHandler';
import { getNormalizedUrl, isValidUrl } from 'common/modules/atoms/validation';

const GetStarted = () => {
	const context = useContext(RegisterContext);
	const { t } = useTranslation();
	const isAuthenticated = useSelector(ViewSelectors.isAuthenticated);
	const isAuthLoading = useSelector(ViewSelectors.isAuthLoading);
	const [websiteUrl, setWebsiteUrl] = useState<string | undefined>();
	const [websiteUrlError, setWebsiteUrlError] = useState<boolean>();
	const [customerPersona, setCustomerPersona] = useState<CustomerPersona | undefined>();
	const [industryCategories, setIndustryCategories] = useState<IndustryCategory[]>([]);

	const handleWebsiteUrlChange = async (value: string) => {
		setWebsiteUrl(value);
		if (!!websiteUrlError) {
			setWebsiteUrlError(!validateWebsiteUrl(value));
		}
		try {
			logEvent('register_action', {
				input: 'website_url',
				data: value,
			});
		} catch (err) {
			errorHandler.report(err);
		}
	};

	const validateWebsiteUrl = (value: string | undefined) => {
		const normalizedUrl = getNormalizedUrl(value, {
			defaultProtocol: 'https',
		});
		return isValidUrl(normalizedUrl, {
			allowEmpty: true,
			allowedProtocols: ['http', 'https'],
			requireTLD: true,
		});
	};

	const handleCustomerPersonaChange = async (value: CustomerPersona) => {
		setCustomerPersona(value);
		try {
			logEvent('register_action', {
				input: 'customer_persona',
				data: value,
			});
		} catch (err) {
			errorHandler.report(err);
		}
	};

	const handleIndustryCategoriesChange = async (value: IndustryCategory[]) => {
		setIndustryCategories(value);
		try {
			logEvent('register_action', {
				input: 'industry_categories',
				data: value,
			});
		} catch (err) {
			errorHandler.report(err);
		}
	};

	const handleAnalyticsUpdates = () => {
		try {
			logEvent('register_action', {
				input: 'continue',
			});
			newAdditionalSignUpInfoEvent({
				website_url: getNormalizedUrl(websiteUrl, { defaultProtocol: 'https' }),
				customer_persona: customerPersona,
				industry_categories: industryCategories,
				email: context.email,
			});
		} catch (err) {
			errorHandler.report(err);
		}
	};

	const handleContinue = () => {
		handleAnalyticsUpdates();
		try {
			if (!!context.shopAnalyticsDocId) {
				api().shopAnalytics.doc(context.shopAnalyticsDocId).update({
					industryCategories,
					customerPersona,
				});
			}
			if (!!context.shopId) {
				api()
					.shops.doc(context.shopId)
					.public.update({
						websiteUrl: getNormalizedUrl(websiteUrl, { defaultProtocol: 'https' }),
					});
			}
		} catch (err) {
			errorHandler.report(err);
		}
		push(Routes.HOME_VIEW);
	};

	if (context.registrationStatus === 'initial') {
		if (isAuthLoading) return null;
		if (isAuthenticated) {
			return <Redirect to={Routes.HOME_VIEW} />;
		}
		return <Redirect to={Routes.REGISTER} />;
	}

	return (
		<Container>
			<Box
				sx={{
					position: 'fixed',
					top: -10,
					left: 0,
					right: 0,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					zIndex: 0,
				}}
			>
				<Box
					component="img"
					sx={{
						width: 1080,
						height: 173,
						maxWidth: '100%',
						objectFit: 'cover',
					}}
					src={gradientPattern}
					alt=""
				/>
			</Box>
			<Box
				sx={{
					width: 400,
					maxWidth: '100%',
					mx: 'auto',
					position: 'relative',
					zIndex: 1,
				}}
			>
				<motion.div
					initial={{ opacity: 0, transitionDuration: '0.3s' }}
					animate={{ opacity: 1, transitionDuration: '0.3s' }}
				>
					<Box mb={5} mt={4} width="100%" display="flex" flexDirection="column" alignItems="center">
						<RentleLogo width={120} grayscale={true} type="black" />
					</Box>
				</motion.div>
				<motion.div
					initial={{
						opacity: 0,
						transform: 'translateY(40px)',
						transitionDuration: '0.6s',
						transitionDelay: '0.3s',
					}}
					animate={{
						opacity: 1,
						transform: 'translateY(0)',
						transitionDuration: '0.6s',
						transitionDelay: '0.3s',
					}}
				>
					{context.registrationStatus === 'error' ? (
						<RegistrationError onBack={() => context.setRegistrationStatus('initial')} />
					) : (
						<>
							<Typography variant="h5" textAlign="center" fontSize="2.8rem" sx={{ mb: 1 }}>
								{t('getStarted.title', "Let's get you started")}
							</Typography>
							<Typography variant="body1" color="secondary" textAlign="center">
								{t(
									'getStarted.description',
									"While we're getting your store ready, please answer a few questions so we can get to know each other a bit better.",
								)}
							</Typography>
							<Stack direction="column" alignItems="center" sx={{ mt: 5 }} spacing={2}>
								<CustomerPersonaPicker
									value={customerPersona}
									onChange={handleCustomerPersonaChange}
								/>
								<TextField
									label={`${t(
										'register.whatIsYourUrl',
										'If you have a website, what is the URL',
									)}?`}
									placeholder={t('register.websiteUrlPlaceholder', 'https://www.example.com')}
									error={!!websiteUrlError}
									onBlur={() => {
										const isValid = validateWebsiteUrl(websiteUrl);
										setWebsiteUrlError(!isValid);
									}}
									helperText={
										!!websiteUrlError
											? t('register.enterValidWebsiteUrl', 'Please enter a valid website URL')
											: ''
									}
									value={websiteUrl}
									onChange={(e) => handleWebsiteUrlChange(e.target.value)}
									variant="outlined"
									fullWidth
									sx={{ maxWidth: 400 }}
								/>
								<IndustrySelector
									label={t('register.industryCategory', 'Pick your industry')}
									variant="outlined"
									value={industryCategories}
									onChange={handleIndustryCategoriesChange}
								/>
								{context.registrationStatus !== 'done' ? (
									<Box mt={6}>
										<Typography variant="body1" color="secondary" textAlign="center">
											{t('getStarted.creatingYourStore', 'Creating your store...')}
										</Typography>
									</Box>
								) : (
									<motion.div
										style={{ width: '100%' }}
										initial={{
											opacity: 0,
											transform: 'translateY(40px)',
											transitionDuration: '0.3s',
										}}
										animate={{
											opacity: 1,
											transform: 'translateY(0)',
											transitionDuration: '0.3s',
										}}
									>
										<Box sx={{ mt: 6 }}>
											<Button
												sx={{
													background: (theme) => theme.palette.common.black,
													'&:hover': {
														background: (theme) => theme.palette.primary.dark,
													},
												}}
												fullWidth
												color="primary"
												variant="contained"
												onClick={handleContinue}
												disabled={websiteUrlError}
											>
												{t('getStarted.enterMyStore', 'Enter my store')}
											</Button>
										</Box>
									</motion.div>
								)}
							</Stack>
						</>
					)}
				</motion.div>
			</Box>
		</Container>
	);
};

const RegistrationError = ({ onBack }: { onBack: () => void }) => {
	const { t } = useTranslation();
	return (
		<>
			<Typography variant="h5" textAlign="center" fontSize="2.8rem" sx={{ mb: 1 }}>
				{t('common:errors.somethingWentWrongShort', 'Something went wrong')}
			</Typography>
			<Typography variant="body1" color="secondary" textAlign="center">
				{t(
					'getStarted.errorDescription',
					'Unfortunately we encountered an unexpected error while creating your store... Please go back to the previous page to try again. Apologies for the inconvenience!',
				)}
			</Typography>
			<Button
				sx={{
					mt: 7,
					background: (theme) => theme.palette.common.black,
					'&:hover': {
						background: (theme) => theme.palette.primary.dark,
					},
				}}
				color="primary"
				variant="contained"
				onClick={onBack}
			>
				{t('common:actions.takeMeBack', 'Take me back')}
			</Button>
		</>
	);
};

export default GetStarted;
